import React from 'react';
import {DragDropContext, Draggable, Droppable} from '@hello-pangea/dnd';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {v4 as uuid} from 'uuid';
import {MAX_NUMBER_OF_LIKERT_SCALE_QUESTIONS} from '../../../../../constants';
import {IValidationErrorObject} from '../../../../../incase-domain/common/validationErrorResponse';
import Button from '../../../../../incase-domain/components/button/Button';
import {IElementLikertScale} from '../../../../../incase-domain/elements/question/likertScale';
import {MAX_LIKERT_QUESTION_LENGTH} from '../../../../../incase-domain/validators/elements/constant';
import {questionValidator} from '../../../../../incase-domain/validators/elements/likertScaleQuestion';
import {isDeleteButtonDisabled} from '../../../../../utils/utils';
import {generateErrorMessages, getErrorClasses} from '../../../../../utils/validation';
import SectionTitle from '../../../../elements/common/SectionTitle';
import DebouncedTextInput from '../../common/debounced/DebouncedTextInput';
import {changeLikertScaleItemText, removeLikertScaleItem, swapLikertScaleItemOrderHandler} from './utils/utils';

interface IHighlightedLikertScaleQuestionsComponentProps {
    highlightedElement: IElementLikertScale;
    errors: IValidationErrorObject | null;
    highlightedElementChangeHandler: {
        single: <T extends IElementLikertScale>(field: keyof T | string, value: unknown) => void;
        multiple: <T extends IElementLikertScale>(fields: (keyof T)[], values: unknown[]) => void;
    };
}

function LikertScaleQuestions(props: IHighlightedLikertScaleQuestionsComponentProps): JSX.Element {
    const {highlightedElement, errors, highlightedElementChangeHandler} = props;
    const {t} = useTranslation();

    function addLikertScaleQuestion(): void {
        if (highlightedElement.questions.length >= MAX_NUMBER_OF_LIKERT_SCALE_QUESTIONS) {
            toast.error(
                `${t('presEditPage.editSidebar.elements.likertScaleQuestion.errorMessage.tooManyQuestions', {
                    maxQuestions: MAX_NUMBER_OF_LIKERT_SCALE_QUESTIONS,
                })}`
            );
            return;
        }
        const newLikertScaleQuestionText = `${t('presEditPage.editSidebar.elements.initialStates.question')} ${
            highlightedElement.questions.length + 1
        }`;
        const newLikertScaleQuestion = [
            ...highlightedElement.questions,
            {
                id: uuid(),
                text: newLikertScaleQuestionText,
            },
        ];
        highlightedElementChangeHandler.single('questions', newLikertScaleQuestion);
    }

    return (
        <div className="l-group--form">
            <div className={`l-group__item ${getErrorClasses(errors, 'questions')}`}>
                <SectionTitle
                    label={t('presEditPage.editSidebar.elements.likertScaleQuestion.questions')}
                    leading={
                        <Button
                            className="c-sequenceQuestionElementOrder__shuffleButton"
                            variant="ghost"
                            size="2xs"
                            color="primary"
                            onClick={() => {
                                addLikertScaleQuestion();
                            }}
                            leading="ph:plus-bold"
                        >
                            {t('designSidepanel.new')}
                        </Button>
                    }
                />

                {generateErrorMessages(errors, `questions`)}

                <DragDropContext
                    onDragEnd={(e) => {
                        if (typeof e.destination?.index !== 'undefined')
                            swapLikertScaleItemOrderHandler(
                                'questions',
                                e.destination.index,
                                e.source.index,
                                highlightedElement,
                                highlightedElementChangeHandler
                            );
                    }}
                >
                    <Droppable droppableId={`liker-scale-${highlightedElement.id}`}>
                        {(provided) => {
                            return (
                                <div {...provided.droppableProps} ref={provided.innerRef} className="c-likertScale__wrapper">
                                    {highlightedElement.questions.map((question, index) => {
                                        return (
                                            <Draggable key={`${index}`} draggableId={`${index}-${question.id}`} index={index}>
                                                {(provided) => {
                                                    return (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            key={`highlighted-element-likert-scale-item-${index}`}
                                                        >
                                                            <div
                                                                className={`${getErrorClasses(errors, `questions.${index}.text`)}`}
                                                                key={question.id}
                                                            >
                                                                <DebouncedTextInput
                                                                    value={question.text}
                                                                    onChange={(value) =>
                                                                        changeLikertScaleItemText(
                                                                            'questions',
                                                                            index,
                                                                            value,
                                                                            highlightedElement,
                                                                            highlightedElementChangeHandler
                                                                        )
                                                                    }
                                                                    validator={(val) => {
                                                                        const validation = questionValidator.shape.text.safeParse(val);
                                                                        return validation.success;
                                                                    }}
                                                                    className="c-sidepanel__input"
                                                                    maxLength={MAX_LIKERT_QUESTION_LENGTH}
                                                                    leading="material-symbols:drag-indicator"
                                                                    trailing={
                                                                        <Button
                                                                            leading="solar:trash-bin-minimalistic-linear"
                                                                            variant="ghost"
                                                                            size="xs"
                                                                            color="error"
                                                                            square
                                                                            disableHover
                                                                            disabled={isDeleteButtonDisabled(
                                                                                highlightedElement.questions.length,
                                                                                2
                                                                            )}
                                                                            onClick={() => {
                                                                                removeLikertScaleItem(
                                                                                    'questions',
                                                                                    index,
                                                                                    highlightedElement,
                                                                                    highlightedElementChangeHandler
                                                                                );
                                                                            }}
                                                                        />
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                }}
                                            </Draggable>
                                        );
                                    })}
                                    {provided.placeholder}
                                </div>
                            );
                        }}
                    </Droppable>
                </DragDropContext>
            </div>
        </div>
    );
}

export default LikertScaleQuestions;
